/*
 * Equipment is the parent container asset — only its children (tiers,
 * panel components, instruments) are tracked in Logistics. Equipment must
 * never reach a Logistics query/update payload, so this allow-list is
 * enforced at the payload builders below, the single choke point every
 * Logistics network call passes through on the client.
 */
const TRACKED_LOGISTICS_OBJECT_TYPES = new Set([
  "tier",
  "panelcomponent",
  "instrument",
]);

export const isTrackedLogisticsObjectType = (objectType) =>
  TRACKED_LOGISTICS_OBJECT_TYPES.has(objectType);

export const isTrackedLogisticsAsset = (item) =>
  Boolean(item) && isTrackedLogisticsObjectType(item.objectType);

const getProgressIdentityKey = (item = {}) =>
  item.uid || `${item.objectType || ""}:${item.objectId || ""}`;

const getProgressObjectType = (progress = {}) =>
  progress.objectType || progress.object_type || "";

const getProgressObjectId = (progress = {}) =>
  String(progress.objectId || progress.object_id || "");

const slugifyTypeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const getTypePrefix = (asset = {}, source = "") => {
  if (source === "asset") return "asset-type";
  if (asset.objectType === "instrument") return "instrument-type";
  if (asset.objectType === "panelcomponent") return "asset-type";
  if (asset.objectType === "equipment" || asset.objectType === "tier") {
    return "equipment-type";
  }

  return "type";
};

const buildTypeMetadata = ({ id, name, prefix }) => {
  const typeName = String(name || "").trim();
  const typeId = id != null && id !== "" ? String(id) : slugifyTypeName(typeName);

  if (!typeId && !typeName) return null;

  return {
    id: typeId.includes(":") ? typeId : `${prefix}:${typeId}`,
    name: typeName || typeId,
  };
};

export const getLogisticsAssetTypeMetadata = (asset = {}) => {
  const prefix = getTypePrefix(asset);

  if (asset.type && typeof asset.type === "object") {
    return buildTypeMetadata({
      id: asset.type.id,
      name: asset.type.name,
      prefix,
    });
  }

  if (typeof asset.type === "string") {
    return buildTypeMetadata({ name: asset.type, prefix });
  }

  const assetTypeDetail = asset.asset_type_details;

  if (assetTypeDetail && typeof assetTypeDetail === "object") {
    return buildTypeMetadata({
      id: assetTypeDetail.id,
      name: assetTypeDetail.name,
      prefix: getTypePrefix(asset, "asset"),
    });
  }

  if (asset.assetType || asset.assetTypeName) {
    return buildTypeMetadata({
      id: asset.assetType,
      name: asset.assetTypeName,
      prefix: getTypePrefix(asset, "asset"),
    });
  }

  const detail =
    asset.type_details ||
    asset.equipment_type_details ||
    asset.instrument_type_details ||
    asset.equipmentType ||
    asset.equipment_type ||
    asset.instrumentType ||
    asset.instrument_type;

  if (detail && typeof detail === "object") {
    return buildTypeMetadata({
      id: detail.id,
      name: detail.name,
      prefix,
    });
  }

  return buildTypeMetadata({
    id: asset.equipmentType || asset.equipment_type,
    name:
      asset.equipmentTypeName || asset.instrumentTypeName,
    prefix,
  });
};

const buildAssetProgressRef = (asset, { includeType = false } = {}) => {
  const ref = {
    uid: asset.uid,
    objectType: asset.objectType,
    objectId: String(asset.objectId),
  };

  if (!includeType) return ref;

  const type = getLogisticsAssetTypeMetadata(asset);

  return type ? { ...ref, type } : ref;
};

export const buildLogisticsProgressQueryPayload = (
  assets = [],
  { includeStatistics = false } = {},
) => ({
  assets: assets.map((asset) =>
    buildAssetProgressRef(asset, { includeType: true }),
  ),
  ...(includeStatistics ? { includeStatistics: true } : {}),
});

export const buildBulkProgressUpdatePayload = ({
  selectedAssets = [],
  logistics = {},
}) => ({
  assets: selectedAssets.map(buildAssetProgressRef),
  logistics: {
    mainStage: logistics.mainStage,
    milestone: logistics.milestone,
    status: logistics.status,
    progressPercent: Number(logistics.progressPercent ?? 0),
    notes: logistics.notes?.trim() || "",
    detailText: logistics.detailText?.trim() || null,
    detailDate: logistics.detailDate || null,
  },
});

export const normalizeLogisticsProgressResult = (progress = {}) => ({
  uid:
    progress.uid ||
    (getProgressObjectType(progress) && getProgressObjectId(progress)
      ? `${getProgressObjectType(progress)}:${getProgressObjectId(progress)}`
      : ""),
  objectType: getProgressObjectType(progress),
  objectId: getProgressObjectId(progress),
  logisticsStage: progress.mainStage || "NOT_STARTED",
  logisticsMilestone: progress.milestone || "",
  logisticsStatus: progress.status || "NOT_STARTED",
  logisticsProgressPercent: Number(progress.progressPercent ?? 0),
  logisticsNotes: progress.notes || "",
  logisticsDetailText: progress.detailText || "",
  logisticsDetailDate: progress.detailDate || "",
  logisticsUpdatedAt: progress.updatedAt || "",
  logisticsUpdatedByName: progress.updatedByName || "",
});

const getResultsFromProgressQueryResponse = (data = {}) => {
  const results = Array.isArray(data) ? data : data.results || [];

  return Array.isArray(results)
    ? results.map(normalizeLogisticsProgressResult)
    : [];
};

const normalizeStageStatistic = (stage = {}) => ({
  stage:
    stage.stage ||
    stage.Stage ||
    stage.mainStage ||
    stage.main_stage ||
    "NOT_STARTED",
  milestone:
    stage.milestone ||
    stage.Milestone ||
    stage.currentMilestone ||
    stage.current_milestone ||
    "",
  count: Number(stage.count ?? stage.Count ?? 0),
  missingProgressCount: Number(
    stage.missingProgressCount ??
      stage.missing_progress_count ??
      stage.MissingProgressCount ??
      0,
  ),
});

const normalizeTypeStatistic = (group = {}) => {
  const type = group.type && typeof group.type === "object" ? group.type : {};
  const typeName = String(type.name || group.typeName || "").trim();
  const typeId = String(type.id || group.typeId || "").trim();

  return {
    type: {
      id: typeId || "unclassified",
      name: typeName || "Unclassified",
    },
    total: Number(group.total ?? 0),
    missingProgressCount: Number(
      group.missingProgressCount ?? group.missing_progress_count ?? 0,
    ),
    stages: Array.isArray(group.stages)
      ? group.stages.map(normalizeStageStatistic)
      : [],
  };
};

export const normalizeLogisticsProgressStatistics = (statistics) => {
  if (!statistics || typeof statistics !== "object") return null;

  return {
    totalAssets: Number(statistics.totalAssets ?? statistics.total_assets ?? 0),
    missingProgressCount: Number(
      statistics.missingProgressCount ??
        statistics.missing_progress_count ??
        0,
    ),
    byStage: Array.isArray(statistics.byStage)
      ? statistics.byStage.map(normalizeStageStatistic)
      : [],
    byType: Array.isArray(statistics.byType)
      ? statistics.byType.map(normalizeTypeStatistic)
      : [],
  };
};

export const normalizeLogisticsProgressQueryResponse = getResultsFromProgressQueryResponse;

export const normalizeLogisticsProgressQueryData = (data = {}) => ({
  results: getResultsFromProgressQueryResponse(data),
  statistics: Array.isArray(data)
    ? null
    : normalizeLogisticsProgressStatistics(data.statistics),
});

// Turns an ISO timestamp (e.g. "2026-07-20T13:35:22.903518Z") into
// "YYYY/MM/DD HH:mm" — matching the format Django's DateTimeField already
// renders "Created" with (`format='%Y/%m/%d %H:%M'`), so both fields read
// consistently in the Info modals. Falls back to the raw value if it can't
// be parsed.
const formatLastUpdatedTimestamp = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;

  const pad = (value) => String(value).padStart(2, "0");

  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Compares the Django app's `modified` timestamp against the Logistics
// service's `logisticsUpdatedAt` timestamp and returns whichever is more
// recent, along with the user who made that change. Returns null when
// neither source has a usable timestamp.
export const getLastAssetUpdate = (asset = {}) => {
  const djangoAt = asset.modified ? new Date(asset.modified) : null;
  const logisticsAt = asset.logisticsUpdatedAt
    ? new Date(asset.logisticsUpdatedAt)
    : null;
  const djangoValid = djangoAt && !Number.isNaN(djangoAt.getTime());
  const logisticsValid = logisticsAt && !Number.isNaN(logisticsAt.getTime());

  if (logisticsValid && (!djangoValid || logisticsAt > djangoAt)) {
    return {
      source: "logistics",
      sourceLabel: "Logistics",
      at: formatLastUpdatedTimestamp(asset.logisticsUpdatedAt),
      by: asset.logisticsUpdatedByName || "",
    };
  }

  if (djangoValid) {
    return {
      source: "django",
      sourceLabel: "Asset",
      at: formatLastUpdatedTimestamp(asset.modified),
      by: asset.last_modified_by || asset.created_by || "",
    };
  }

  return null;
};

export const mergeLogisticsProgressIntoAssets = (
  assets = [],
  progressResults = [],
) => {
  const progressLookup = new Map(
    progressResults.map((progress) => [
      getProgressIdentityKey(progress),
      progress,
    ]),
  );

  return assets.map((asset) => {
    const progress = progressLookup.get(getProgressIdentityKey(asset));

    if (!progress) {
      return {
        ...asset,
        logisticsStage: asset.logisticsStage || "NOT_STARTED",
        logisticsMilestone: asset.logisticsMilestone || "",
        logisticsStatus: asset.logisticsStatus || "NOT_STARTED",
        logisticsProgressPercent: Number(asset.logisticsProgressPercent ?? 0),
        logisticsDetailText: asset.logisticsDetailText || "",
        logisticsDetailDate: asset.logisticsDetailDate || "",
      };
    }

    return {
      ...asset,
      ...progress,
    };
  });
};
